import React, { useEffect, useRef, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import { FormHandles } from "@unform/core"
import { Form } from "@unform/web"
import * as Yup from "yup"

import { useTranslation, useValidation } from "hooks"
import { translations } from "./translations"

import * as S from "./styles"
import { MainContainer, Modal, Alert } from "components/shared"
import { Input, Select, InputMask } from "components/shared/Form"

import { useMutation, useQuery, useQueryClient } from "react-query"
import { apiGeneral, notify } from "services"
import TeamRelations from "components/settings/CommercialTeam/TeamRelations"

interface IParams {
  id: string
}

export const EditCommercialTeams: React.FC = () => {
  const { id } = useParams() as IParams
  const formRef = useRef<FormHandles>(null)
  const { handleFormErrors } = useValidation()
  const { getTranslation } = useTranslation(translations)
  const [currentCommercialRoleId, setCurrentCommercialRoleId] = useState<number | null>(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<any>(null)
  const queryClient = useQueryClient()

  const schemaValidation = Yup.object().shape({
    erp_code: Yup.string().required(getTranslation("erpCodeIsRequired")),
    name: Yup.string().required(getTranslation("nameIsRequired")),
    phone_number: Yup.string().required(
      getTranslation("phoneNumberIsRequired")
    ),
    commercial_role_id: Yup.string().required(
      getTranslation("commercialRoleIsRequired")
    ),
    tracking_region_id: Yup.string().required(
      getTranslation("trackingRegionIsRequired")
    ),
  })

  const { data, isLoading } = useQuery({
    queryKey: ["get-commercial-team"],
    queryFn: async () => {
      const { data } = await apiGeneral.get(`/comercial-team/${id}`)
      return data
    },
  })

  const { mutate, isLoading: isPending } = useMutation({
    mutationKey: ["edit-commercial-team"],
    mutationFn: async (data: any) => {
      const response = await apiGeneral.put(`/comercial-team/${id}`, data)
      return response.data
    },
  })

  const { data: trackingRegions, isLoading: isLoadingTrackingRegions } =
    useQuery({
      queryKey: ["get-tracking-regions"],
      queryFn: async () => {
        const { data } = await apiGeneral.get("/tracking-region")
        return data
      },
      select: (data) => {
        return data.map((d: any) => ({ label: d.name, value: d.id }))
      },
    })

  const { data: commercialRoles, isLoading: isLoadingCommercialRoles } =
    useQuery({
      queryKey: ["get-commercial-roles"],
      queryFn: async () => {
        const { data } = await apiGeneral.get("/comercial-role")
        return data
      },
      select: (data) => {
        return data.map((d: any) => ({ label: d.name, value: d.id }))
      },
    })

  const history = useHistory()

  const getTeamRelationsTitle = (commercialRoleId: number) => {
    switch (commercialRoleId) {
      case 10:
        return getTranslation("contatosPrincipais")
      case 4:
        return getTranslation("gerentes")
      default:
        return getTranslation("contatosPrincipais")
    }
  }

  const processSubmit = (formData: any) => {
    mutate(formData, {
      onSuccess: () => {
        notify("success", getTranslation("commercialTeamUpdatedSuccessfully"))
        // Invalidate and refetch the commercial team data
        queryClient.invalidateQueries(["get-commercial-team"])
        // Invalidate all TeamRelations queries for this commercial team
        queryClient.invalidateQueries([`get-commercial-team-relations-${id}`])
        // Invalidate all commercial team options queries for all possible roleIds
        queryClient.invalidateQueries([`get-commercial-team-options-${id}-10`])
        queryClient.invalidateQueries([`get-commercial-team-options-${id}-6`])
        queryClient.invalidateQueries([`get-commercial-team-options-${id}-11`])
        queryClient.invalidateQueries([`get-commercial-team-options-${id}-4`])
        queryClient.invalidateQueries([`get-commercial-team-options-${id}-9`])
        // Clean up pending data
        setPendingFormData(null)
      },
      onError: () => {
        notify("error", getTranslation("errorUpdatingCommercialTeam"))
        // Clean up pending data
        setPendingFormData(null)
      },
    })
  }

  const handleConfirmSubmit = () => {
    if (pendingFormData) {
      setConfirmModalOpen(false)
      processSubmit(pendingFormData)
    }
  }

  const handleSubmit = async (formData: any) => {
    try {
      await schemaValidation.validate(formData, {
        abortEarly: false,
      })

      const newCommercialRoleId = formData.commercial_role_id?.value || formData.commercial_role_id
      const hasCommercialRoleChanged = data?.commercial_role_id &&
        newCommercialRoleId !== data.commercial_role_id
      if (hasCommercialRoleChanged && data.commercial_role_id !== 9) {
        setPendingFormData(formData)
        setConfirmModalOpen(true)
        return
      }

      processSubmit(formData)
    } catch (error) {
      handleFormErrors(error, formRef)
    }
  }

  useEffect(() => {
    if (data && trackingRegions && commercialRoles) {
      const setCommercialRoleId = commercialRoles.find(
        (cr: any) => cr.value === data.commercial_role_id
      )

      const setTrackingRegionId = trackingRegions.find(
        (cr: any) => cr.value === Number(data.tracking_region_id)
      )

      formRef?.current?.setData(data)
      formRef?.current?.setFieldValue("commercial_role_id", setCommercialRoleId)
      formRef?.current?.setFieldValue("tracking_region_id", setTrackingRegionId)

      // Set initial commercial role id
      setCurrentCommercialRoleId(data.commercial_role_id)
    }
  }, [data, trackingRegions, commercialRoles])

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation("configuracoes")}{" "}
          <span>{getTranslation("editCommercialTeam")}</span>
          {isLoading && <S.LoadingPage />}
        </h1>
        <S.HeaderButtons>
          <S.ButtonMini btStyle="dark" onClick={() => history.goBack()}>
            <S.IconArrowLeft />
            {getTranslation("voltar")}
          </S.ButtonMini>
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          placeholder=""
          onPointerEnterCapture={() => { }}
          onPointerLeaveCapture={() => { }}
        >
          <S.BoxContainer>
            <S.FormRow>
              <Input name="erp_code" label={getTranslation("codigoSAP")} />
              <Input name="name" label={getTranslation("nomeFantasia")} />
              <InputMask
                name="phone_number"
                label={getTranslation("telefone")}
                mask="(99) 9999-99999"
              />
            </S.FormRow>
            <S.FormRow>
              <Select
                name="commercial_role_id"
                label={getTranslation("commercialRole")}
                isLoading={isLoadingCommercialRoles}
                isDisabled={isLoadingCommercialRoles}
                options={commercialRoles ?? []}
                placeholder={getTranslation("selecione")}
              />
              <Select
                name="tracking_region_id"
                label={getTranslation("trackingRegion")}
                isLoading={isLoadingTrackingRegions}
                isDisabled={isLoadingTrackingRegions}
                options={trackingRegions ?? []}
                placeholder={getTranslation("selecione")}
              />
            </S.FormRow>
          </S.BoxContainer>
          {currentCommercialRoleId && currentCommercialRoleId !== 9 && (
            <TeamRelations
              commercialTeamId={id}
              roleId={currentCommercialRoleId}
              title={getTeamRelationsTitle(currentCommercialRoleId)}
            />
          )}
          <S.FormFooter>
            <S.FormRow>
              <S.Button
                btStyle="cancel"
                type="button"
                onClick={() => history.goBack()}
              >
                {getTranslation("cancelar")}
              </S.Button>
              <S.Button type="submit">
                {isPending ? <S.Loading /> : getTranslation("atualizar")}
              </S.Button>
            </S.FormRow>
          </S.FormFooter>
        </Form>
      </S.PageContent>
      <Modal isOpen={confirmModalOpen}>
        <Alert
          title={getTranslation("commercialRoleChangeTitle")}
          text={getTranslation("commercialRoleChangeWarning")}
          close={() => {
            setConfirmModalOpen(false)
            setPendingFormData(null)
          }}
          action={handleConfirmSubmit}
          labelAction={getTranslation("continuar")}
          isLoading={isPending}
        />
      </Modal>
    </MainContainer>
  )
}
